import axios from 'axios'
import authHeader from './auth-header'

// API Base URL - use the same configuration as other services
const API_URL = process.env.VUE_APP_API_URL || 'https://api.4-rah.com'

class PopupMessageService {
  getAdminAuthHeaders() {
    const token = localStorage.getItem('popup_admin_token')
    const baseHeaders = authHeader()
    return {
      ...baseHeaders,
      'X-Admin-Token': token || '',
    }
  }
  // Admin APIs

  // Get all popup messages (for admin panel)
  async getAll() {
    try {
      const response = await axios.post(
        `${API_URL}/admin/popup-message/list`,
        {},
        { headers: this.getAdminAuthHeaders() },
      )
      return response.data
    } catch (error) {
      // console.error('Error fetching popup messages:', error)
      throw error
    }
  }

  // Create new popup message
  async create(messageData) {
    try {
      const response = await axios.post(
        `${API_URL}/admin/popup-message/create`,
        messageData,
        { headers: this.getAdminAuthHeaders() },
      )
      return response.data
    } catch (error) {
      // console.error('Error creating popup message:', error)
      throw error
    }
  }

  // Update popup message
  async update(messageData) {
    try {
      const response = await axios.post(
        `${API_URL}/admin/popup-message/edit`,
        messageData,
        { headers: this.getAdminAuthHeaders() },
      )
      return response.data
    } catch (error) {
      // console.error('Error updating popup message:', error)
      throw error
    }
  }

  // Delete popup message
  async delete(messageId) {
    try {
      const response = await axios.post(
        `${API_URL}/admin/popup-message/delete`,
        { _id: messageId },
        { headers: this.getAdminAuthHeaders() },
      )
      return response.data
    } catch (error) {
      // console.error('Error deleting popup message:', error)
      throw error
    }
  }

  // Toggle message status (active/inactive)
  async toggleStatus(messageId, isActive) {
    try {
      const response = await axios.post(
        `${API_URL}/admin/popup-message/toggle-status`,
        { _id: messageId, isActive },
        { headers: this.getAdminAuthHeaders() },
      )
      return response.data
    } catch (error) {
      // console.error('Error toggling popup message status:', error)
      throw error
    }
  }

  // Client APIs

  // Get active popup message for client app
  async getActiveMessage() {
    try {
      const response = await axios.post(
        `${API_URL}/popup-message/get-active`,
        {},
        { headers: authHeader() },
      )
      return response.data
    } catch (error) {
      // console.error('Error fetching active popup message:', error)
      throw error
    }
  }

  // Mark popup message as seen
  async markAsSeen(messageId) {
    try {
      const response = await axios.post(
        `${API_URL}/popup-message/mark-seen`,
        { messageId },
        { headers: authHeader() },
      )
      return response.data
    } catch (error) {
      // console.error('Error marking popup message as seen:', error)
      throw error
    }
  }

  // Utility methods

  // Validate popup message data
  validateMessage(messageData) {
    const errors = []

    if (!messageData.title || messageData.title.trim().length === 0) {
      errors.push('عنوان الزامی است')
    }
    if (messageData.title && messageData.title.length > 100) {
      errors.push('عنوان نمی‌تواند بیش از 100 کاراکتر باشد')
    }

    if (!messageData.message || messageData.message.trim().length === 0) {
      errors.push('متن پیام الزامی است')
    }
    if (messageData.message && messageData.message.length > 500) {
      errors.push('متن پیام نمی‌تواند بیش از 500 کاراکتر باشد')
    }

    if (!messageData.buttonText || messageData.buttonText.trim().length === 0) {
      errors.push('متن دکمه الزامی است')
    }
    if (messageData.buttonText && messageData.buttonText.length > 50) {
      errors.push('متن دکمه نمی‌تواند بیش از 50 کاراکتر باشد')
    }

    if (messageData.buttonAction === 'redirect' && (!messageData.redirectUrl || messageData.redirectUrl.trim().length === 0)) {
      errors.push('لینک هدایت الزامی است')
    }

    if (messageData.redirectUrl && messageData.redirectUrl.length > 200) {
      errors.push('لینک هدایت نمی‌تواند بیش از 200 کاراکتر باشد')
    }

    if (messageData.priority && (messageData.priority < 1 || messageData.priority > 10)) {
      errors.push('اولویت باید بین 1 تا 10 باشد')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  // Format message data for display
  formatMessageForDisplay(messageData) {
    if (!messageData) return null

    return {
      ...messageData,
      formattedCreationTime: this.formatDate(messageData.creationTime),
      formattedModificationTime: this.formatDate(messageData.modificationTime),
      formattedShowUntil: messageData.showUntil ? this.formatDate(messageData.showUntil) : null,
      priorityText: this.getPriorityText(messageData.priority),
      targetAudienceText: this.getTargetAudienceText(messageData.targetAudience),
      buttonActionText: this.getButtonActionText(messageData.buttonAction),
    }
  }

  // Get priority text
  getPriorityText(priority) {
    const priorityMap = {
      1: 'کم',
      2: 'کم',
      3: 'کم',
      4: 'متوسط',
      5: 'متوسط',
      6: 'متوسط',
      7: 'بالا',
      8: 'بالا',
      9: 'بالا',
      10: 'فوری',
    }
    return priorityMap[priority] || 'نامشخص'
  }

  // Get target audience text
  getTargetAudienceText(audience) {
    const audienceMap = {
      all: 'همه کاربران',
      new_users: 'کاربران جدید',
      existing_users: 'کاربران قدیمی',
      expired_users: 'کاربران منقضی',
    }
    return audienceMap[audience] || 'نامشخص'
  }

  // Get button action text
  getButtonActionText(action) {
    const actionMap = {
      settings: 'رفتن به تنظیمات',
      redirect: 'هدایت به لینک',
      close: 'بستن پاپ‌آپ',
    }
    return actionMap[action] || 'نامشخص'
  }

  // Format timestamp
  formatDate(timestamp) {
    if (!timestamp) return ''

    let date
    if (typeof timestamp === 'number') {
      // Unix timestamp
      date = new Date(timestamp * 1000)
    } else {
      // ISO string
      date = new Date(timestamp)
    }

    return date.toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
}

export default new PopupMessageService()
